import { useState } from 'react';
import { ApiFailure, api, type Project } from '../api.ts';
import { ConfirmDanger } from '../ConfirmDanger.tsx';

/**
 * THE FRONT DOOR. "The project creation is the first step — I create a
 * project and write its name and select its type." One card per project the
 * caller administers, and the create form on the same page, because an empty
 * index that points somewhere else to create is a hallway with no doors.
 *
 * KIND, said in the owner's two words: a `transcribe` project receives audio
 * files; a `record` project produces audio in-house (production tasks — a
 * later phase; the card says so honestly rather than showing dead controls).
 */
export function Projects({
  token,
  projects,
  onCreated,
  onDeleted,
}: {
  token: string;
  projects: Project[];
  onCreated: (projectId: string) => void;
  /** A project was permanently deleted; the list should be re-fetched. */
  onDeleted: () => void;
}): JSX.Element {
  const [creating, setCreating] = useState(projects.length === 0);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'transcribe' | 'record'>('transcribe');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [condemned, setCondemned] = useState<Project | null>(null);

  async function create(): Promise<void> {
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.createProject(token, {
        name: name.trim(),
        kind,
        ...(description.trim() ? { description: description.trim() } : {}),
      });
      setName('');
      setDescription('');
      setCreating(false);
      onCreated(res.projectId);
    } catch (err) {
      setError(err instanceof ApiFailure ? err.message : 'Could not create the project.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <section className="card">
        <div className="card-head">
          <h2>Projects</h2>
          <span className="topbar-spacer" />
          {!creating && (
            <button className="btn-primary" type="button" style={{ width: 'auto' }} onClick={() => setCreating(true)}>
              + Create project
            </button>
          )}
        </div>

        {creating && (
          <div className="adduser">
            <div className="adduser-grid">
              <label className="field">
                <span className="field-label">Name</span>
                <input
                  className="input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Street interviews — Amman"
                  autoFocus
                  disabled={busy}
                />
              </label>
              <label className="field">
                <span className="field-label">Type</span>
                <select value={kind} onChange={(e) => setKind(e.target.value as 'transcribe' | 'record')} disabled={busy}>
                  <option value="transcribe">Transcribe audio — I upload files</option>
                  <option value="record">Record audio — produced in-house</option>
                </select>
              </label>
            </div>
            <label className="field">
              <span className="field-label">Description (optional)</span>
              <input
                className="input"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this project is for"
                disabled={busy}
              />
            </label>
            {error && (
              <div className="notice notice-warn" role="status">
                {error}
              </div>
            )}
            <div className="btn-row">
              {projects.length > 0 && (
                <button className="btn-quiet" type="button" disabled={busy} onClick={() => setCreating(false)}>
                  Cancel
                </button>
              )}
              <button
                className="btn-primary"
                type="button"
                style={{ width: 'auto' }}
                disabled={busy || !name.trim()}
                onClick={() => void create()}
              >
                {busy ? 'Creating…' : 'Create project'}
              </button>
            </div>
          </div>
        )}

        {projects.length === 0 && !creating && (
          <p className="hint" style={{ marginBottom: 0 }}>
            Nothing yet. A project is the first step of everything.
          </p>
        )}
      </section>

      {projects.map((p) => (
        <section className="card" key={p.id}>
          <div className="card-head">
            <h2>
              <a className="btn-link" href={`#/projects/${p.id}`} style={{ padding: 0, fontSize: 'inherit' }}>
                {p.name}
              </a>
            </h2>
            <span className={`pill ${p.kind === 'record' ? 'pill-processing' : 'pill-review'}`}>
              {p.kind === 'record' ? 'record' : 'transcribe'}
            </span>
            <span className="topbar-spacer" />
            <span className="hint-tight num" style={{ margin: 0 }}>
              {p.recordingCount} recording{p.recordingCount === 1 ? '' : 's'} · {p.code} · {p.language}
            </span>
            <button
              className="btn-link btn-link-danger"
              type="button"
              onClick={() => setCondemned(p)}
            >
              Delete
            </button>
          </div>
          {p.description && (
            <p className="hint" style={{ marginBottom: 0 }}>
              {p.description}
            </p>
          )}
        </section>
      ))}

      {condemned && (
        <ConfirmDanger
          title="Delete this whole project for ever?"
          warning={
            <>
              <strong>{condemned.name}</strong> — its {condemned.recordingCount} recording
              {condemned.recordingCount === 1 ? '' : 's'}, every version, every answer, its tasks,
              its calls and its stored audio go, for everyone, permanently. The people keep their
              accounts. There is no undo and no trash.
            </>
          }
          phrase="delete"
          actionLabel="Delete the project for ever"
          busyLabel="Deleting…"
          onRun={() => api.deleteProject(token, condemned.id, 'delete').then(() => undefined)}
          onDone={(done) => {
            setCondemned(null);
            if (done) onDeleted();
          }}
        />
      )}
    </>
  );
}
